import GridBasedCalculationParams from 'interfaces/girdBasedCalculationParams';
import PostProcessingAction, {
  MappingFilterMetadata,
  PostProcessingActionCreated,
} from 'interfaces/postProcessingAction';
import { VisualizationCreated } from 'interfaces/visualization';
import { Dictionary, filter, find, forEach, get, isArray, isPlainObject, map } from 'lodash';
import { ModelPreferencesById } from 'utils/models';
import { NewCell } from '..';
import { convertLogicalQueryToBackend } from '../PostProcessingActions/utils';
import {
  convertFeatureActionsToBackend,
  convertPostProcessingActionsToBackend,
  matchOrInsertCellValueToSessionNewCells,
} from '../utils';
import type { PresetEncapsulation, PresetInputSection } from './presetEncapsulation';
export { getConsumerPresetJson } from './presetEncapsulation';
export type {
  PresetActionAccess,
  PresetActionEncapsulation,
  PresetEncapsulation,
  PresetInputSection,
} from './presetEncapsulation';

export interface HighlightedPresetInput {
  section: PresetInputSection;
  actionInstanceId: string;
  inputKey: string;
  helperText: string;
}

export interface PresetJsonData {
  name?: string;
  postprocessing: PostProcessingActionCreated[];
  features: PostProcessingActionCreated[];
  visualizations: VisualizationCreated[];
  gridBasedCalculationParams: Record<string, GridBasedCalculationParams>;
  modelPreferencesByModelId?: ModelPreferencesById;
  newCellsCreatedValues?: Array<{ stain: string; backendFormattedValue: any }>; // Backend formatted new cell values and their target stain
  highlightedInputs?: HighlightedPresetInput[];
  encapsulation?: PresetEncapsulation;
}

export interface PresetSaveData {
  postProcessingActionsById: Record<string, any>;
  postProcessingActions: PostProcessingActionCreated[];
  featureActions: PostProcessingActionCreated[];
  newCellsCreated?: Record<string, NewCell>;
  visualizations: VisualizationCreated[];
  gridBasedCalculationParams: Record<string, GridBasedCalculationParams>;
  modelPreferencesByModelId: ModelPreferencesById;
  pipRunName: string;
  highlightedInputs: HighlightedPresetInput[];
}

export const getResolvableHighlightedInputs = ({
  highlightedInputs,
  postProcessingActions,
  featureActions,
  postProcessingActionsById,
}: Pick<
  PresetSaveData,
  'highlightedInputs' | 'postProcessingActions' | 'featureActions' | 'postProcessingActionsById'
>): HighlightedPresetInput[] =>
  filter(highlightedInputs, (highlightedInput) => {
    const actions = highlightedInput.section === 'postprocessing' ? postProcessingActions : featureActions;
    const action = find(actions, { id: highlightedInput.actionInstanceId });
    return Boolean(
      action &&
        find((postProcessingActionsById as Dictionary<PostProcessingAction>)[action.actionId]?.inputs, {
          inputKey: highlightedInput.inputKey,
        })
    );
  });

/**
 * Creates preset JSON data from current form state
 */
export const createPresetJsonFromCurrentState = (data: PresetSaveData): PresetJsonData => {
  return {
    postprocessing: convertPostProcessingActionsToBackend(data.postProcessingActionsById, data.postProcessingActions),
    features: convertFeatureActionsToBackend(data.postProcessingActionsById, data.featureActions, data.newCellsCreated),
    visualizations: data.visualizations,
    gridBasedCalculationParams: data.gridBasedCalculationParams,
    modelPreferencesByModelId: data.modelPreferencesByModelId,
    newCellsCreatedValues: map(
      filter(data.newCellsCreated, (cell) => Boolean(cell.value)),
      (cell) => ({
        stain: cell.stain,
        backendFormattedValue: convertLogicalQueryToBackend(cell.value),
      })
    ),
    highlightedInputs: getResolvableHighlightedInputs(data),
  };
};

/**
 * Creates preset JSON data for new preset (includes pipRunName)
 */
export const createNewPresetJson = (data: PresetSaveData): PresetJsonData => {
  return {
    name: data.pipRunName, // PipRunName is only saved when creating a new preset
    ...createPresetJsonFromCurrentState(data),
  };
};

/**
 * Validates that newCellsCreated data is properly preserved in preset JSON
 */
export const validateNewCellsInPreset = (presetJson: PresetJsonData): boolean => {
  // Check if newCellsCreated exists as a separate field
  if (!presetJson.newCellsCreatedValues) {
    return false;
  }

  // Validate structure of each new cell
  for (const [, cell] of Object.entries(presetJson.newCellsCreatedValues)) {
    if (!cell.backendFormattedValue || !cell.stain || typeof cell.stain !== 'string') {
      return false;
    }
  }

  return true;
};

/**
 * Extracts newCellsCreated from preset JSON, with fallback to empty object
 */
export const extractNewCellsFromPreset = (
  presetJson: PresetJsonData,
  getMappingFiltersMetadataForStain: (stain: string) => MappingFilterMetadata[]
): Record<string, NewCell> => {
  if (!presetJson || !presetJson.newCellsCreatedValues) {
    return {};
  }

  // Validate the structure before returning
  const newCells = presetJson.newCellsCreatedValues;
  const legacyNewCells = get(presetJson, 'newCellsCreated');
  if (!isArray(newCells)) {
    return {};
  } else if (legacyNewCells && isPlainObject(legacyNewCells)) {
    // Assume presets with objects are before we applied the newCellsCreatedValues array structure, and just use them directly
    // They will be converted properly once the preset is saved again
    return legacyNewCells as any;
  }

  // Filter out any invalid entries
  const validNewCells: Record<string, NewCell> = {};
  forEach(newCells, (cell) => {
    if (cell?.stain && cell?.backendFormattedValue) {
      matchOrInsertCellValueToSessionNewCells(cell.backendFormattedValue, {
        sessionNewCells: validNewCells,
        stain: cell.stain,
        mappingFiltersMetadata: getMappingFiltersMetadataForStain(cell.stain),
      });
    }
  });

  return validNewCells;
};
